import React, { forwardRef, useLayoutEffect, useRef } from "react"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import SectionThreePartTwo from "@homepage/SectionThreePart2"
import ColorLines from "@homepage/ColorLines"
import Circle from "@homepage/Circle"

import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"

import tw from "twin.macro"
import {
  btnStyles,
  darkPurpleHeading,
  purpleHeading,
  paragraphStyles,
  pSuperStyles,
} from "@styles/ui"

import BlurredAnchor from "@images/homepage/blurred-anchor.inline.svg"
import BlurredAnchorMobile from "@images/homepage/blurred-anchor-mobile.inline.svg"

const SectionThree = forwardRef((props, ref) => {
  const { bgImg, videoFPOThumbnail, location, safetySection } = props

  gsap.registerPlugin(ScrollTrigger)
  const sec3 = ref
  const icon = useRef(null)
  const icon2 = useRef(null)
  const part2 = useRef(null)
  const safetyWrapper = safetySection

  useLayoutEffect(() => {
    const el = icon.current
    const el2 = icon2.current

    let mm = gsap.matchMedia()
    let breakpoint = 1024

    mm.add(
      {
        isDesktop: `(min-width: ${breakpoint}px)`,
        isMobile: `(max-width: ${breakpoint - 1}px)`,
      },
      context => {
        let { isDesktop } = context.conditions

        gsap.fromTo(
          el,
          {
            top: "2.999vh",
            transform: "rotate(0deg)",
          },
          {
            top: "119.94vh",
            transform: "rotate(80deg)",
            scrollTrigger: {
              trigger: safetyWrapper.current,
              start: "top top",
              end: "bottom top",
              scrub: 1,
              pinSpacing: false,
              toggleActions: "restart none none reverse",
            },
          },
          0
        )

        gsap.fromTo(
          el2,
          {
            y: 0,
            rotate: 0,
            scale: 1,
          },
          {
            y: 0,
            scale: isDesktop ? 0.54 : 0.4,
            rotate: 90,
            top: isDesktop ? "-20%" : "-9.25%",
            right: isDesktop ? "-20rem" : "-12rem",
            duration: 2,
            scrollTrigger: {
              trigger: safetyWrapper.current,
              start: "top top",
              end: "60% top",
              scrub: 1,
              pinSpacing: false,
              toggleActions: "restart none none reverse",
            },
          },
          0
        )
      }
    )

    return () => mm.revert()
  }, [safetyWrapper, sec3])

  const safetyStyles = tw`text-white absolute  m-auto top-[50vh] left-0 -translate-y-1/2 pl-[2.875rem] pr-4 lg:(pl-0 pr-0 left-0 right-0 w-[42rem] -translate-y-1/2 text-center) xl:(top-1/2)`
  const buttonStyle = [btnStyles, tw`transition-all duration-300`]
  const anchorOneStyles = tw`absolute top-5 left-12 w-[135.375px] h-auto rotate-[9deg] lg:(w-[230px]) xl:(w-[270px])`

  return (
    <div
      ref={sec3}
      id="sectionThree"
      className="hp-sections"
      tw="relative w-full overflow-hidden"
    >
      <div
        id="green-circle-lines-mobile"
        tw="h-[90%] absolute top-0 left-[20px] z-10 lg:(hidden) xl:h-full"
      >
        <ColorLines green />
      </div>

      <GatsbyImage
        className="section-three-bg-img"
        alt=""
        tw="w-full h-[90%] !absolute top-0 left-0 xl:h-full"
        image={bgImg}
        objectFit="cover"
      />

      <section
        ref={safetyWrapper}
        id="safety-wrapper"
        tw="h-[100vh] w-full overflow-hidden relative lg:(h-screen)"
      >
        <div tw="hidden absolute top-0 left-[0.5px] w-full h-[15vh] lg:block">
          <Circle tw="bg-green absolute top-[14.5vh] left-1/2 -translate-x-1/2" />
          <ColorLines
            green
            tw="[div.lines]:(mobileOnly:(mr-0.5 border-l-[2.5px]) h-[99%])"
          />
        </div>
        <div className="safety" css={[safetyStyles, tw`pr-14 lg:p-0`]}>
          <div tw="relative">
            <Circle tw="bg-green absolute top-[2px] -left-[32px] md:top-[3px] lg:hidden" />
            <h2 css={[purpleHeading, tw`text-tan`]}>SAFETY</h2>
          </div>

          <h3
            css={[
              darkPurpleHeading,
              tw`text-green tracking-[0.005rem] md:text-4xl lg:(text-5xl)`,
            ]}
          >
            Trust in a demonstrated
            <br tw="md:hidden" /> safety&nbsp;profile
          </h3>

          <p css={[paragraphStyles]}>
            In a pivotal clinical study, DefenCath was shown to have a similar
            safety profile as standard&#8209;of&#8209;care (SOC) heparin, and
            risk of adverse events were similar<sup css={pSuperStyles}>1,3</sup>
          </p>
          <div tw="my-6 lg:(mt-7)">
            <Link
              to="/safety"
              css={[
                buttonStyle,
                tw`border-purple bg-green text-purple hocus:(bg-purple text-green) md:(block w-60 mt-4) lg:m-auto`,
              ]}
            >
              View safety profile
            </Link>
          </div>
        </div>

        <div tw="hidden absolute bottom-0 left-[0.5px] w-full h-[17vh] lg:block">
          <ColorLines
            green
            tw="[div.lines]:(mobileOnly:(mr-0.5 border-l-[2.5px]) h-full)"
          />
        </div>
      </section>

      <div id="anchor-one" css={anchorOneStyles} ref={icon}>
        <BlurredAnchorMobile tw="absolute w-full h-auto lg:hidden" />
        <BlurredAnchor tw="hidden absolute top-5 left-8 w-auto h-auto lg:(block left-0) xl:(top-[115px]) 3xl:left-52" />
      </div>

      <div ref={part2}>
        <SectionThreePartTwo
          bgImg={bgImg}
          videoFPOThumbnail={videoFPOThumbnail}
          icon={icon}
          icon2={icon2}
          location={location}
        />
      </div>
    </div>
  )
})

export default SectionThree
